import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useMovieContext } from "../context/MovieContext.jsx";
import { useLibraryContext } from "../context/LibraryContext.jsx";
import SearchBar from "../components/SearchBar.jsx";
import GenreFilter from "../components/GenreFilter.jsx";
import MovieCard from "../components/MovieCard.jsx";
import SkeletonCard from "../components/SkeletonCard.jsx";

const HomePage = ({ handleMovieClick }) => {
  const {
    searchQuery,
    activeSearchTerm,
    genresMap,
    selectedGenre,
    handleGenreChange,
    handleSearchChange,
    handleSearchSubmit,
    searchInputRef,
    isLoadingMovies,
    isLoadingMore,
    movieFetchError,
    searchResults,
    currentPage,
    totalPages,
    handleLoadMoreMovies,
  } = useMovieContext();

  const { userLibrary, addMovieToLibrary, removeMovieFromLibrary } =
    useLibraryContext();

  const [showGoToTop, setShowGoToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowGoToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleGoToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const genres = Object.entries(genresMap).map(([id, name]) => ({ id, name }));
  const selectedGenreName = genres.find((g) => g.id === selectedGenre)?.name;

  return (
    <>
      {/* Search Section */}
      <section className="mb-12">
        <div className="flex justify-center mb-10">
          <div className="w-full max-w-xl flex items-start space-x-2">
            <div className="w-2/3">
              <SearchBar
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                handleSearchSubmit={handleSearchSubmit}
                searchInputRef={searchInputRef}
              />
            </div>
            <div className="w-1/3">
              <GenreFilter
                genres={genres}
                selectedGenre={selectedGenre}
                onGenreChange={handleGenreChange}
              />
            </div>
          </div>
        </div>

        {isLoadingMovies && searchResults.length === 0 ? (
          <div className="flex justify-center w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        ) : movieFetchError ? (
          <p className="text-center text-red-500 text-lg mt-8">
            {movieFetchError}
          </p>
        ) : searchResults.length > 0 ? (
          <div className="flex justify-center w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              <h2 className="col-span-full text-3xl font-semibold text-white mb-4">
                {activeSearchTerm
                  ? `Search Results for "${activeSearchTerm}"`
                  : selectedGenreName
                  ? `${selectedGenreName} Movies`
                  : "Popular Movies"}
              </h2>
              {searchResults.map((movie, index) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  index={index}
                  onCardClick={handleMovieClick}
                  onAddToLibrary={addMovieToLibrary}
                  onRemoveFromLibrary={removeMovieFromLibrary}
                  userLibrary={userLibrary}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-400 text-lg mt-8">
            {activeSearchTerm
              ? `No movies found for "${activeSearchTerm}". Try a different search!`
              : selectedGenreName
              ? `No movies found for the genre "${selectedGenreName}".`
              : "No popular movies found."}
          </p>
        )}

        {/* Load More Button */}
        {!isLoadingMore &&
          searchResults.length > 0 &&
          currentPage < totalPages && (
            <div className="flex justify-center mt-8">
              <motion.button
                onClick={handleLoadMoreMovies}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#e50914] hover:bg-[#b0060f] text-white font-bold py-3 px-8 transition duration-300 ease-in-out shadow-lg"
              >
                Load More
              </motion.button>
            </div>
          )}

        {/* Loading more movies - show skeleton cards */}
        {isLoadingMore && (
          <div className="flex justify-center w-full mt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={`loading-more-${i}`} />
              ))}
            </div>
          </div>
        )}
      </section>
      {showGoToTop && (
        <motion.button
          onClick={handleGoToTop}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-8 right-8 z-50 bg-[#e50914] hover:bg-[#b0060f] text-white p-4 rounded-full shadow-lg transition duration-300 ease-in-out flex items-center justify-center"
          title="Go to Top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 15l7-7 7 7"
            />
          </svg>
        </motion.button>
      )}
    </>
  );
};

export default HomePage;
